import React, { useState, useEffect } from 'react';
import {
  Paper,
  Box,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  CircularProgress,
  Autocomplete,
  Container
} from '@mui/material';
import { Message as MessageIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import {
  buildHarvestContactPayload,
  buildHarvestLocationsEndpoint,
} from '../utils/harvestLocations';
import { useAuth } from '../context/AuthContext';
import { getCurrentOrganization } from '../utils/organizationContext';
import FormOrganizationBanner from './FormOrganizationBanner';

function PublicHarvestForm() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    phone_number: '',
    email: '',
    harvest_location_id: '',
    custom_location: '',
    evangelism_date: new Date(),
    collector_name: '',
    collector_phone: ''
  });

  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const { organizationSlug } = useAuth();
  const { organizationSlug: urlSlug } = useParams();
  const [currentOrgSlug, setCurrentOrgSlug] = useState(null);

  // Get organization from URL
  useEffect(() => {
    const slug = urlSlug || organizationSlug || getCurrentOrganization();
    setCurrentOrgSlug(slug);
    
    // If no organization found and not in an organization route, redirect to welcome page
    if (!slug && !organizationSlug && window.location.pathname === '/harvest') {
      window.location.href = '/';
    }
  }, [urlSlug, organizationSlug]);

  useEffect(() => {
    // Only fetch locations if we have an organization slug
    if (currentOrgSlug) {
      fetchLocations();
    }
  }, [currentOrgSlug]);

  const fetchLocations = async () => {
    if (!currentOrgSlug) {
      return; // Don't fetch if no organization slug yet
    }
    
    try {
      setLoading(true);
      setMessage({ type: '', text: '' }); // Clear any previous errors
      
      const url = `${process.env.REACT_APP_API_URL || 'http://localhost:3003'}${buildHarvestLocationsEndpoint(currentOrgSlug)}`;
      const response = await axios.get(url);
      setLocations(response.data || []);
      
      // If no locations found, show a helpful message
      if (!response.data || response.data.length === 0) {
        setMessage({ type: 'info', text: 'No harvest locations configured for this organization yet. Please contact your administrator.' });
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
      setMessage({ type: 'error', text: 'Failed to load locations. Please ensure you are accessing this form from the correct organization URL.' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Function to check for duplicates
  const checkForDuplicate = async (data) => {
    try {
      setIsCheckingDuplicate(true);
      setDuplicateInfo(null);
      
      const checkData = {
        email: data.email || '',
        phone_number: data.phone_number || '',
        evangelism_date: data.evangelism_date,
        ...(currentOrgSlug && { organization_slug: currentOrgSlug })
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3003'}/api/harvest/contacts/check-duplicate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(checkData),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.isDuplicate) {
          setDuplicateInfo(result.duplicate);
          return true; // Duplicate found
        }
      }
      return false; // No duplicate
    } catch (error) {
      console.error('Error checking for duplicate:', error);
      return false; // Continue with submission if check fails
    } finally {
      setIsCheckingDuplicate(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone_number || !formData.evangelism_date) {
      setMessage({ type: 'error', text: 'Please fill in all required fields' });
      return;
    }

    // Check if either a location is selected OR "Other" is chosen with custom name
    if (!formData.harvest_location_id && !(formData.harvest_location_id === 'other' && formData.custom_location)) {
      setMessage({ type: 'error', text: 'Please select a location or choose "Other" with a custom location name' });
      return;
    }

    try {
      setSubmitting(true);
      setMessage({ type: '', text: '' });
      setDuplicateInfo(null);
      
      // First check for duplicates
      const isDuplicate = await checkForDuplicate(formData);
      if (isDuplicate) {
        return; // Stop submission if duplicate found
      }

      const submissionData = buildHarvestContactPayload(formData, currentOrgSlug, locations);

      if (!currentOrgSlug) {
        setMessage({ type: 'error', text: 'Organization context required. Please access this form from an organization-specific URL.' });
        setSubmitting(false);
        return;
      }
      
      const url = `${process.env.REACT_APP_API_URL || 'http://localhost:3003'}/api/harvest/contacts?organization_slug=${currentOrgSlug}`;
      const response = await axios.post(url, submissionData);

      setMessage({ type: 'success', text: 'Contact added successfully! Thank you for your outreach work!' });
      
      // Reset form
      setFormData({
        name: '',
        phone_number: '',
        email: '',
        harvest_location_id: '',
        custom_location: '',
        evangelism_date: new Date(),
        collector_name: '',
        collector_phone: ''
      });
    } catch (error) {
      console.error('Error adding contact:', error);
      
      // Handle duplicate error from backend
      if (error.response?.status === 409) {
        setDuplicateInfo(error.response.data.duplicate);
        setMessage({ type: 'error', text: error.response.data.message || 'This contact has already been registered for this date.' });
        return;
      }
      
      const errorMessage = error.response?.data?.error || error.message || 'Failed to add contact';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Harvest Outreach Contact Form
        </Typography>
        <FormOrganizationBanner subtitle="Harvest outreach contacts for this church branch" />
        <Typography variant="body1" color="text.secondary" gutterBottom align="center">
          Thank you for your evangelism work! Please enter the contact details you collected.
        </Typography>
      </Box>

      <Paper sx={{ p: 4 }}>
        {message.text && (
          <Alert severity={message.type} sx={{ mb: 3 }}>
            {message.text}
          </Alert>
        )}

        {duplicateInfo && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Duplicate Contact Detected
            </Typography>
            <Typography variant="body2">
              A contact already exists for <strong>{duplicateInfo.name}</strong> on <strong>{duplicateInfo.evangelism_date}</strong>.
            </Typography>
            {duplicateInfo.email && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Email: {duplicateInfo.email}
              </Typography>
            )}
            {duplicateInfo.phone && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Phone: {duplicateInfo.phone}
              </Typography>
            )}
            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
              If this is a different person, please contact the church office for assistance.
            </Typography>
          </Alert>
        )}

        {isCheckingDuplicate && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Checking for existing contacts...
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contact Name *"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone Number *"
                value={formData.phone_number}
                onChange={(e) => handleInputChange('phone_number', e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email (Optional)"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Autocomplete
                options={locations.filter(loc => loc.name !== 'Other')}
                getOptionLabel={(option) => option.name}
                value={locations.find(loc => loc.id === formData.harvest_location_id) || null}
                onChange={(event, newValue) => {
                  if (newValue) {
                    handleInputChange('harvest_location_id', newValue.id);
                  } else {
                    handleInputChange('harvest_location_id', '');
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Harvest Location *"
                    required={formData.harvest_location_id !== 'other'}
                    placeholder="Search or select location"
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Typography>{option.name}</Typography>
                  </li>
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Or Select Other</InputLabel>
                <Select
                  value={formData.harvest_location_id === 'other' ? 'other' : ''}
                  onChange={(e) => {
                    if (e.target.value === 'other') {
                      handleInputChange('harvest_location_id', 'other');
                    } else {
                      handleInputChange('harvest_location_id', '');
                    }
                  }}
                  label="Or Select Other"
                >
                  <MenuItem value="other">Other (Custom Location)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {formData.harvest_location_id === 'other' && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Custom Location Name *"
                  value={formData.custom_location || ''}
                  onChange={(e) => handleInputChange('custom_location', e.target.value)}
                  placeholder="Enter location name"
                  required
                />
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Evangelism Date *"
                  value={formData.evangelism_date}
                  onChange={(date) => handleInputChange('evangelism_date', date)}
                  renderInput={(params) => <TextField {...params} fullWidth required />}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Collector Name (Optional)"
                value={formData.collector_name}
                onChange={(e) => handleInputChange('collector_name', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Collector Phone (Optional)"
                value={formData.collector_phone}
                onChange={(e) => handleInputChange('collector_phone', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" justifyContent="center">
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={submitting || isCheckingDuplicate}
                  startIcon={(submitting || isCheckingDuplicate) ? <CircularProgress size={20} /> : null}
                  sx={{ px: 4, py: 1.5 }}
                >
                  {isCheckingDuplicate ? 'Checking...' : (submitting ? 'Adding Contact...' : 'Submit Contact')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
}

export default PublicHarvestForm;
